import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getReviews } from "../../network/service/NutritionService";
import { SOURCE_NAMES } from "../products/nutrition/nutrition-model";
import ReviewAccess from "./review-access";
import { gradeName } from "../products/nutrition/grade-names";

const TABS = [
  ["PENDING", "Waiting for review"],
  ["APPROVED", "Approved"],
  ["REJECTED", "Rejected"],
  ["ALL", "All"],
];

export const SOURCE_GROUPS = [
  ["USDA", "USDA"],
  ["FATSECRET", "FatSecret"],
  ["EATPROTEIN", "EatProtein"],
  ["FSSAI", "FSSAI label"],
];

export const REVIEW_LABELS = { PENDING: "Waiting", APPROVED: "Approved", REJECTED: "Rejected" };

export const formatDate = (d) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";

/**
 * Nutrition experts check every product that has nutrition data and
 * approve or reject it. Filter by review state and by who the values come
 * from (USDA, FatSecret, EatProtein team, FSSAI pack label).
 */
function NutritionReview() {
  const navigate = useNavigate();
  const [review, setReview] = useState("PENDING");
  const [source, setSource] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getReviews({ review, source, categoryId, q, page, limit: 25 }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [review, source, categoryId, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pick = (setter) => (value) => {
    setter(value);
    setCategoryId("");
    setPage(1);
  };

  return (
    <main className="container-fluid dashboard">
      <div className="nutri-panel">
        <div className="nutri-header">
          <div>
            <h4>Nutrition review</h4>
            <p className="nutri-muted mb-0">
              Check each product's nutrition, correct it if needed, then approve or reject it. Approved
              products show as verified to customers.
            </p>
          </div>
        </div>

        <div className="nutri-tabs">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              className={`nutri-tab${review === value ? " active" : ""}`}
              onClick={() => pick(setReview)(value)}
            >
              {label} <span className="nutri-tab-count">{data ? data.counts[value] : ""}</span>
            </button>
          ))}
        </div>

        <div className="nutri-row nutri-filters">
          <div className="review-sources" role="group" aria-label="Data source">
            <span className="nutri-muted">Source:</span>
            <button
              type="button"
              className={`review-source${source === "" ? " active" : ""}`}
              onClick={() => pick(setSource)("")}
            >
              All
            </button>
            {SOURCE_GROUPS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`review-source${source === value ? " active" : ""}`}
                onClick={() => pick(setSource)(value)}
              >
                {label} <span className="nutri-tab-count">{data ? data.sourceCounts[value] : ""}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="nutri-row nutri-filters">
          <input
            className="form-control"
            placeholder="Search product name or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="form-select"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All categories</option>
            {data?.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error} <button className="nutri-link" onClick={load}>Retry</button>
          </div>
        )}

        <table className="table table-sm nutri-worklist">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Source</th>
              <th>Grade</th>
              <th>Review</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={6} className="nutri-muted">Loading…</td></tr>
            )}
            {data?.items.length === 0 && (
              <tr><td colSpan={6} className="nutri-muted">No products here.</td></tr>
            )}
            {data?.items.map((item) => (
              <tr key={item.productId} className={loading ? "nutri-stale" : ""}>
                <td>
                  <strong>{item.name}</strong>
                  <div className="nutri-muted">#{item.productId}</div>
                </td>
                <td>{item.categoryName}</td>
                <td>{SOURCE_NAMES[item.source] ?? item.source}</td>
                <td>
                  {item.grade && (
                    <span className="nutri-chip" style={{ background: item.color }}>
                      {item.grade === "UNRATED" ? "Not rated" : gradeName(item.grade)}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`review-badge review-${item.review.toLowerCase()}`}>
                    {REVIEW_LABELS[item.review]}
                  </span>
                  {item.reviewedByName && (
                    <div className="nutri-muted">
                      by {item.reviewedByName} · {formatDate(item.reviewedAt)}
                    </div>
                  )}
                  {item.reviewNote && <div className="nutri-muted review-note">“{item.reviewNote}”</div>}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-purple-bg"
                    onClick={() => navigate(`/nutrition-review/${item.productId}`)}
                  >
                    {item.review === "PENDING" ? "Review" : "Open"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data && data.totalPages > 1 && (
          <div className="nutri-actions">
            <button className="btn btn-white-bg" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span className="nutri-muted">
              Page {data.page} of {data.totalPages} · {data.total} products
            </span>
            <button className="btn btn-white-bg" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

export default function NutritionReviewPage() {
  return (
    <ReviewAccess>
      <NutritionReview />
    </ReviewAccess>
  );
}
